import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Gem, Target } from 'lucide-react'
import { toast } from 'react-hot-toast'
import {
  createWheel500MockEngine,
  WHEEL500_COLORS,
  WHEEL500_COLOR_KEYS,
  WHEEL500_PHASES,
  WHEEL500_SECTORS,
  WHEEL500_TIMINGS
} from '../mock/wheel500Mock'
import Wheel500Strip from '../components/Wheel500Strip'
import Wheel500BetsFeed from '../components/Wheel500BetsFeed'

const QUICK_AMOUNTS = [100, 500, 1000]

const DESIGN_VARIANTS = [
  { key: 'esports', label: 'Esports Dashboard' },
  { key: 'casino', label: 'Casino Premium' },
  { key: 'minimal', label: 'Minimal Neon' }
]

const DESIGN_THEME = {
  esports: {
    titleClass: 'text-cyan-300',
    frameClass: 'relative rounded-3xl border border-cyan-400/20 bg-slate-950/55 p-3 shadow-[0_20px_60px_rgba(6,182,212,0.15)]',
    phaseClass: 'rounded-full border border-cyan-400/30 bg-cyan-500/10 px-4 py-1.5 text-sm text-cyan-100',
    quickActiveClass: 'border-cyan-300 bg-cyan-400/20 text-cyan-100',
    quickIdleClass: 'border-white/10 bg-surface text-text-secondary hover:text-white',
    betCardClass: 'backdrop-blur-sm shadow-[0_8px_20px_rgba(6,182,212,0.12)]',
    recentChipClass: 'ring-1 ring-cyan-300/30'
  },
  casino: {
    titleClass: 'text-amber-300',
    frameClass: 'relative rounded-3xl border border-amber-300/20 bg-gradient-to-b from-amber-500/10 via-rose-500/10 to-transparent p-3 shadow-[0_22px_70px_rgba(245,158,11,0.14)]',
    phaseClass: 'rounded-full border border-amber-300/35 bg-amber-500/15 px-4 py-1.5 text-sm text-amber-100',
    quickActiveClass: 'border-amber-300 bg-amber-400/20 text-amber-100',
    quickIdleClass: 'border-white/10 bg-surface text-text-secondary hover:text-white',
    betCardClass: 'shadow-[0_8px_24px_rgba(245,158,11,0.18)]',
    recentChipClass: 'ring-1 ring-amber-300/35'
  },
  minimal: {
    titleClass: 'text-white',
    frameClass: 'relative rounded-3xl border border-white/10 bg-black/20 p-3',
    phaseClass: 'rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-sm text-white/90',
    quickActiveClass: 'border-accent-primary bg-accent-primary/15 text-accent-primary',
    quickIdleClass: 'border-white/10 bg-surface text-text-secondary hover:text-white',
    betCardClass: 'shadow-none',
    recentChipClass: 'ring-1 ring-white/15'
  }
}

const Wheel500 = () => {
  const [roundState, setRoundState] = useState(null)
  const [amount, setAmount] = useState(QUICK_AMOUNTS[0])
  const [designVariant, setDesignVariant] = useState('esports')
  const [countdownMs, setCountdownMs] = useState(WHEEL500_TIMINGS.BETTING_MS)
  const engineRef = useRef(null)
  const intervalRef = useRef(null)

  // Запускаем мок-движок
  useEffect(() => {
    const engine = createWheel500MockEngine({
      onUpdate: (next) => setRoundState(next)
    })
    engineRef.current = engine
    return () => {
      engine.destroy()
      engineRef.current = null
    }
  }, [])

  // Локальный таймер обратного отсчёта фазы
  useEffect(() => {
    if (!roundState) return
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    const update = () => {
      const elapsed = Date.now() - roundState.phaseStartedAt
      setCountdownMs(Math.max(0, roundState.phaseDurationMs - elapsed))
    }
    update()
    intervalRef.current = setInterval(update, 100)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [roundState?.roundId, roundState?.status])

  const status = roundState?.status
  const isBetting = status === WHEEL500_PHASES.BETTING
  const isResult = status === WHEEL500_PHASES.RESULT
  const sums = roundState?.sums || { blue: 0, green: 0, purple: 0, red: 0, gold: 0 }
  const bets = roundState?.bets || []
  const recentResults = roundState?.recentResults || []
  const activeTheme = DESIGN_THEME[designVariant] || DESIGN_THEME.esports

  // Сумма моих ставок по цветам в текущем раунде
  const myColorAmounts = useMemo(() => {
    const acc = { blue: 0, green: 0, purple: 0, red: 0, gold: 0 }
    bets.forEach((b) => {
      if (b.isMine) acc[b.color] = (acc[b.color] || 0) + b.amount
    })
    return acc
  }, [bets])

  const handlePlaceBet = (color) => {
    if (!engineRef.current) return
    if (!isBetting) {
      toast.error('Ставки уже закрыты')
      return
    }
    const result = engineRef.current.placeBet({ color, amount })
    if (!result.ok) {
      toast.error('Не удалось поставить ставку')
    }
  }

  const phaseLabel = useMemo(() => {
    if (status === WHEEL500_PHASES.BETTING) return `Принимаем ставки · ${(countdownMs / 1000).toFixed(1)}с`
    if (status === WHEEL500_PHASES.SPINNING) return 'Колесо крутится…'
    if (status === WHEEL500_PHASES.RESULT && roundState?.winColor) {
      const c = WHEEL500_COLORS[roundState.winColor]
      return `Результат: ${c.label} ×${c.multiplier}`
    }
    return ''
  }, [status, countdownMs, roundState?.winColor])

  return (
    <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8 overflow-x-hidden">
      <div className="mx-auto max-w-5xl space-y-5">
        {/* Заголовок */}
        <div className="flex items-center justify-center gap-2">
          <h1 className={`flex items-center gap-2 text-2xl lg:text-4xl font-bold ${activeTheme.titleClass}`}>
            <Target className="w-6 h-6 lg:w-8 lg:h-8 shrink-0" />
            <span>Wheel 500</span>
          </h1>
        </div>

        <div className="rounded-2xl border border-white/10 bg-surface/40 p-2.5">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-xs font-semibold text-white">Вариант дизайна</p>
            <span className="text-[11px] text-text-secondary">Preview</span>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {DESIGN_VARIANTS.map((variant) => {
              const isActive = designVariant === variant.key
              return (
                <button
                  key={variant.key}
                  type="button"
                  onClick={() => setDesignVariant(variant.key)}
                  className={`rounded-xl border px-3 py-2 text-left transition-all ${
                    isActive
                      ? 'border-accent-primary bg-accent-primary/20 text-white shadow-[0_0_18px_rgba(0,255,127,0.22)]'
                      : 'border-white/10 bg-dark-primary/45 text-text-secondary hover:text-white'
                  }`}
                >
                  <span className="text-xs font-semibold">{variant.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        <div className={activeTheme.frameClass}>

        {/* Фаза + таймер */}
        <div className="flex items-center justify-center">
          <div className={activeTheme.phaseClass}>
            {phaseLabel}
          </div>
        </div>

        {/* Лента секторов */}
        <Wheel500Strip
          sectors={WHEEL500_SECTORS}
          status={status}
          winSectorIndex={roundState?.winSectorIndex}
          roundId={roundState?.roundId}
          designVariant={designVariant}
        />

        {/* Последние результаты */}
        {recentResults.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-1.5">
            <span className="text-xs text-text-secondary mr-1">Последние:</span>
            {recentResults.slice(0, 10).map((r, idx) => {
              const c = WHEEL500_COLORS[r.color]
              return (
                <span
                  key={`${idx}-${r.sectorIndex}`}
                  className={`inline-flex h-7 min-w-[34px] items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white ${c.bgClass} ${activeTheme.recentChipClass}`}
                  title={`#${r.sectorIndex} ${c.label} ×${c.multiplier}`}
                >
                  ×{r.multiplier}
                </span>
              )
            })}
          </div>
        )}

        {/* Выбор суммы быстрой ставки */}
        <div className="flex items-center justify-center gap-2">
          <span className="text-xs text-text-secondary">Ставка:</span>
          {QUICK_AMOUNTS.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => setAmount(a)}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm font-semibold transition-colors ${
                amount === a
                  ? activeTheme.quickActiveClass
                  : activeTheme.quickIdleClass
              }`}
            >
              {a} <Gem className="h-3 w-3" />
            </button>
          ))}
        </div>

        {/* Кнопки ставок по цветам */}
        <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
          {WHEEL500_COLOR_KEYS.map((colorKey) => {
            const cfg = WHEEL500_COLORS[colorKey]
            const isWinner = isResult && roundState?.winColor === colorKey
            const isLoser = isResult && roundState?.winColor && roundState.winColor !== colorKey
            const mySum = myColorAmounts[colorKey] || 0
            const total = sums[colorKey] || 0

            return (
              <button
                key={colorKey}
                type="button"
                onClick={() => handlePlaceBet(colorKey)}
                disabled={!isBetting}
                className={`relative overflow-hidden rounded-2xl border px-3 py-3 text-left transition-all ${cfg.softClass} ${activeTheme.betCardClass} ${
                  isWinner ? `ring-2 ${cfg.ringClass} animate-pulse` : ''
                } ${isLoser ? 'opacity-40' : ''} ${
                  isBetting ? 'hover:-translate-y-0.5 hover:brightness-110 active:translate-y-0' : 'cursor-not-allowed'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-bold ${cfg.textClass}`}>
                    {cfg.label}
                  </span>
                  <span className="rounded-full bg-black/30 px-2 py-0.5 text-xs font-bold text-white">
                    ×{cfg.multiplier}
                  </span>
                </div>

                <div className="mt-2 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1 text-white text-sm font-semibold">
                    {total.toLocaleString()} <Gem className="h-3 w-3 text-accent-primary" />
                  </span>
                  {mySum > 0 && (
                    <span className="text-[10px] font-semibold text-white/80">
                      моя: {mySum.toLocaleString()}
                    </span>
                  )}
                </div>
              </button>
            )
          })}
        </div>

        {/* Лента ставок */}
        <Wheel500BetsFeed bets={bets} status={status} winColor={roundState?.winColor} designVariant={designVariant} />
        </div>
      </div>
    </div>
  )
}

export default Wheel500
